import { useState } from "react";
import { Link2, AlertTriangle, CheckCircle, XCircle, Loader2, ExternalLink, Shield, ArrowRight, Clock, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface RedirectHop {
  url: string;
  statusCode: number;
  location?: string;
}

interface LinkValidationResult {
  originalUrl: string;
  finalUrl: string;
  isReachable: boolean;
  statusCode: number | null;
  redirectChain: RedirectHop[];
  redirectCount: number;
  issues: {
    type: "error" | "warning" | "info";
    message: string;
  }[];
  analysis: {
    isShortener: boolean;
    hasSuspiciousTld: boolean;
    usesHttps: boolean;
    matchesBlocklist: boolean;
    hasMismatchedDomain: boolean;
    responseTime: number;
  };
  safetyScore: number;
}

export const LinkValidator = () => {
  const [url, setUrl] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [result, setResult] = useState<LinkValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sampleUrls = [
    "https://bit.ly/3xyzABC",
    "https://google.com",
    "https://github.com/itdukes",
    "http://example.com",
  ];

  const loadSample = () => {
    const sample = sampleUrls[Math.floor(Math.random() * sampleUrls.length)];
    setUrl(sample);
    setResult(null);
    setError(null);
  };

  const validateLink = async () => {
    if (!url.trim()) {
      toast.error("Please enter a URL to validate");
      return;
    }

    setIsValidating(true);
    setResult(null);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("validate-link", {
        body: { url: url.trim() },
      });

      if (fnError) throw fnError;

      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
      }
    } catch (err) {
      console.error("Error validating link:", err);
      setError("Failed to validate link. Please try again.");
    } finally {
      setIsValidating(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-success";
    if (score >= 50) return "text-warning";
    return "text-destructive";
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return "bg-success/10 border-success/20";
    if (score >= 50) return "bg-warning/10 border-warning/20";
    return "bg-destructive/10 border-destructive/20";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return "Safe";
    if (score >= 50) return "Caution";
    return "Risky";
  };

  const getStatusCodeColor = (code: number) => {
    if (code >= 200 && code < 300) return "text-success";
    if (code >= 300 && code < 400) return "text-primary";
    if (code >= 400) return "text-destructive";
    return "text-muted-foreground";
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Link2 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Link Validator
          </h3>
          <p className="text-sm text-muted-foreground">
            Check URLs for safety, redirects, and blocklists
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              URL to Validate
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSample}
              className="text-xs text-primary hover:text-primary/80"
            >
              Load Sample
            </Button>
          </div>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setResult(null);
                setError(null);
              }}
              placeholder="https://example.com/link"
              className="pl-10"
              onKeyDown={(e) => e.key === "Enter" && validateLink()}
            />
          </div>
        </div>

        <Button
          onClick={validateLink}
          disabled={isValidating || !url.trim()}
          className="w-full"
        >
          {isValidating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking link...
            </>
          ) : (
            <>
              <Shield className="w-4 h-4 mr-2" />
              Validate Link
            </>
          )}
        </Button>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4">
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="w-5 h-5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Safety Score */}
            <div className={`rounded-xl border p-5 ${getScoreBg(result.safetyScore)}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Safety Score</p>
                  <p className={`text-4xl font-bold ${getScoreColor(result.safetyScore)}`}>
                    {result.safetyScore}
                    <span className="text-lg text-muted-foreground">/100</span>
                  </p>
                </div>
                <div className={`px-4 py-2 rounded-full ${getScoreBg(result.safetyScore)} border border-current/20`}>
                  <span className={`text-sm font-bold ${getScoreColor(result.safetyScore)}`}>
                    {getScoreLabel(result.safetyScore)}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className={`p-3 rounded-xl border text-center ${result.isReachable ? "bg-success/10 border-success/20" : "bg-destructive/10 border-destructive/20"}`}>
                {result.isReachable ? (
                  <CheckCircle className="w-5 h-5 text-success mx-auto mb-1" />
                ) : (
                  <XCircle className="w-5 h-5 text-destructive mx-auto mb-1" />
                )}
                <p className="text-xs font-medium text-foreground">Reachable</p>
              </div>

              <div className={`p-3 rounded-xl border text-center ${result.analysis.usesHttps ? "bg-success/10 border-success/20" : "bg-warning/10 border-warning/20"}`}>
                {result.analysis.usesHttps ? (
                  <CheckCircle className="w-5 h-5 text-success mx-auto mb-1" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-warning mx-auto mb-1" />
                )}
                <p className="text-xs font-medium text-foreground">HTTPS</p>
              </div>

              <div className={`p-3 rounded-xl border text-center ${!result.analysis.isShortener ? "bg-success/10 border-success/20" : "bg-warning/10 border-warning/20"}`}>
                {!result.analysis.isShortener ? (
                  <CheckCircle className="w-5 h-5 text-success mx-auto mb-1" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-warning mx-auto mb-1" />
                )}
                <p className="text-xs font-medium text-foreground">Direct Link</p>
              </div>

              <div className="p-3 rounded-xl border text-center bg-muted/30 border-border">
                <Clock className="w-5 h-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xs font-medium text-foreground">{result.analysis.responseTime}ms</p>
              </div>
            </div>

            {/* Redirect Chain */}
            {result.redirectChain.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-primary" />
                  Redirect Chain ({result.redirectCount} redirect{result.redirectCount !== 1 ? 's' : ''})
                </h4>
                <div className="space-y-1">
                  {result.redirectChain.map((hop, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border text-xs"
                    >
                      <span className={`font-mono font-bold ${getStatusCodeColor(hop.statusCode)}`}>
                        {hop.statusCode || 'ERR'}
                      </span>
                      <span className="text-muted-foreground truncate flex-1 font-mono">
                        {hop.url}
                      </span>
                      {index < result.redirectChain.length - 1 && (
                        <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Final Destination */}
            {result.originalUrl !== result.finalUrl && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">Final Destination</h4>
                <a
                  href={result.finalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1 break-all"
                >
                  {result.finalUrl}
                  <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
              </div>
            )}

            {/* Issues */}
            {result.issues.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Analysis Results</h4>
                <div className="space-y-2">
                  {result.issues.map((issue, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-2 p-3 rounded-lg border ${
                        issue.type === "error"
                          ? "bg-destructive/5 border-destructive/20"
                          : issue.type === "warning"
                          ? "bg-warning/5 border-warning/20"
                          : "bg-success/5 border-success/20"
                      }`}
                    >
                      {issue.type === "error" ? (
                        <XCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                      ) : issue.type === "warning" ? (
                        <AlertTriangle className="w-4 h-4 text-warning mt-0.5 shrink-0" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-success mt-0.5 shrink-0" />
                      )}
                      <p className="text-sm text-foreground">{issue.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
